import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowRight,
  Shield,
  Search,
  Star,
  Info,
  X,
  Sparkles,
  Tag,
  User,
  ChevronDown,
  ChevronUp,
  Briefcase,
  Wrench,
  Keyboard,
  SearchX,
  Zap,
  Mail,
  LogOut,
  LayoutGrid,
  List,
  Building2
} from 'lucide-react';

import { usePermissions } from '../hooks/usePermissions';
import { resolveIcon, resolveTagIcon } from '../utils/iconMap';
import './LandingPage.css';

const VIEW_MODE_STORAGE_KEY = 'pinnacle-view-mode';
const VIEW_MODES = ['cards', 'list'];

// An app is client/tenant-specific when it is tagged to one or more customers
const isClientApp = (app) => Array.isArray(app.customers) && app.customers.length > 0;

// Search helpers. Terms like 'P&ID', 'Q&A' or 'AI-Powered' should be found by 'pid', 'qa' or 'ai powered',
// so every comparison is done on both the plain lowercase text and an alphanumeric-only form.
const normalizeForSearch = (value) => String(value || '').toLowerCase().replace(/[^a-z0-9]+/g, '');

const collectSearchTerms = (app) => [
  app.name,
  typeof app.description === 'string' ? app.description : '',
  app.category,
  app.author,
  ...(app.tags || []),
  ...(app.keywords || []),
  ...(app.customers || [])
].filter(Boolean).map((term) => String(term).toLowerCase());

const matchesSearchQuery = (app, query) => {
  const tokens = query.toLowerCase().split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return true;
  const terms = collectSearchTerms(app);
  const plain = terms.join(' ');
  const compact = normalizeForSearch(plain);
  const normalizedTerms = terms.map(normalizeForSearch);
  // Every token must match somewhere: as typed, or with punctuation/spaces removed (per term or across the text)
  return tokens.every((token) => {
    const compactToken = normalizeForSearch(token);
    return plain.includes(token)
      || (compactToken && (normalizedTerms.some((t) => t.includes(compactToken)) || compact.includes(compactToken)));
  });
};

const matchesTag = (tag, selected) => normalizeForSearch(tag) === normalizeForSearch(selected);


const LandingPage = ({ darkMode }) => {
  const { isAuthenticated, isLoading, loginWithRedirect, user, logout } = useAuth0();
  const searchInputRef = useRef(null);
  const navigate = useNavigate();

  // Permissions + app catalog hook
  const { hasAppAccess, appCatalog, loading: permissionsLoading, accessDenied, trackAppLaunch } = usePermissions();

  // Extract app data from server response (empty arrays while loading)
  const applications = useMemo(() => appCatalog?.applications || [], [appCatalog]);
  const workplace = useMemo(() => appCatalog?.workplace || [], [appCatalog]);
  const toolbox = useMemo(() => appCatalog?.toolbox || [], [appCatalog]);
  const companyBranding = appCatalog?.companyBranding || {};
  const popularTags = appCatalog?.popularTags || [];

  // State management
  const [searchQuery, setSearchQuery] = useState('');
  const [favorites, setFavorites] = useState([]);
  const [recentApps, setRecentApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [undoNotification, setUndoNotification] = useState(null);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [selectedTag, setSelectedTag] = useState(null);
  const [showGuide, setShowGuide] = useState(null);
  const [showWorkplace, setShowWorkplace] = useState(true); // Workplace section expanded by default
  const [showToolbox, setShowToolbox] = useState(true); // Toolbox section expanded by default
  const [showKeyboardHelp, setShowKeyboardHelp] = useState(false); // Keyboard shortcuts help
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [visibleCards, setVisibleCards] = useState(new Set());
  const [viewMode, setViewMode] = useState(() => {
    const saved = localStorage.getItem(VIEW_MODE_STORAGE_KEY);
    return VIEW_MODES.includes(saved) ? saved : 'cards';
  });

  const changeViewMode = (mode) => {
    if (!VIEW_MODES.includes(mode)) return;
    setViewMode(mode);
    localStorage.setItem(VIEW_MODE_STORAGE_KEY, mode);
  };

  // Class applied to every card container so list view restyles cards without touching their markup
  const gridClass = `cards-grid view-${viewMode}`;

  // Memoize allApps to prevent unnecessary re-renders
  const allApps = useMemo(() => [...applications, ...workplace, ...toolbox], [applications, workplace, toolbox]);

  // Split client/tenant-tagged apps out of their home sections into a dedicated section
  const commonApplications = useMemo(() => applications.filter((app) => !isClientApp(app)), [applications]);
  const commonWorkplace = useMemo(() => workplace.filter((app) => !isClientApp(app)), [workplace]);
  const commonToolbox = useMemo(() => toolbox.filter((app) => !isClientApp(app)), [toolbox]);
  const clientApps = useMemo(() => allApps.filter(isClientApp), [allApps]);
  const [showClientApps, setShowClientApps] = useState(true); // Client section expanded by default

  // Sanitize search input - prevent XSS and injection attacks
  const sanitizeInput = useCallback((input) => {
    if (!input) return '';
    return input
      .replace(/[<>"'`();{}[\]\\]/g, '') // Remove potentially dangerous characters
      .substring(0, 100); // Limit length to prevent DoS
  }, []);

  // Load preferences from localStorage
  useEffect(() => {
    setLoading(true);
    
    const savedFavorites = JSON.parse(localStorage.getItem('pinnacle-favorites') || '[]');
    const savedRecent = JSON.parse(localStorage.getItem('pinnacle-recent') || '[]');
    
    setFavorites(savedFavorites);
    setRecentApps(savedRecent);
    setLoading(false);
  }, []);

  // Auto-redirect to Auth0 login if not authenticated
  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      loginWithRedirect();
    }
  }, [isLoading, isAuthenticated, loginWithRedirect]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyPress = (e) => {
      // / key focuses search
      if (e.key === '/' && !e.ctrlKey && !e.metaKey && document.activeElement !== searchInputRef.current) {
        e.preventDefault();
        searchInputRef.current?.focus();
      }
      // ? key shows keyboard shortcuts help
      if (e.key === '?' && !e.ctrlKey && !e.metaKey && document.activeElement !== searchInputRef.current) {
        e.preventDefault();
        setShowKeyboardHelp(prev => !prev);
      }
      // Esc clears search, closes modals
      if (e.key === 'Escape') {
        if (showGuide) {
          setShowGuide(null);
        } else if (showKeyboardHelp) {
          setShowKeyboardHelp(false);
        } else if (searchQuery) {
          setSearchQuery('');
          setSelectedIndex(-1);
        }
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [searchQuery, showGuide, showKeyboardHelp]);

  const handleAppClick = (app) => {
    // Check if it's an internal route
    if (app.internal) {
      navigate(app.url);
      return;
    }

    // Track recent apps
    const newRecent = [app.id, ...recentApps.filter(id => id !== app.id)].slice(0, 5);
    setRecentApps(newRecent);
    localStorage.setItem('pinnacle-recent', JSON.stringify(newRecent));
    
    // Track usage analytics
    const analytics = JSON.parse(localStorage.getItem('pinnacle-analytics') || '{}');
    analytics[app.id] = {
      count: (analytics[app.id]?.count || 0) + 1,
      lastUsed: Date.now()
    };
    localStorage.setItem('pinnacle-analytics', JSON.stringify(analytics));

    // MAU signal for Manage App Access — no-ops server-side for unmanaged apps.
    trackAppLaunch(app.id);

    window.open(app.url, '_blank', 'noopener,noreferrer');
  };

  const toggleFavorite = (appId, e) => {
    if (e) e.stopPropagation();
    
    const isRemoving = favorites.includes(appId);
    const newFavorites = isRemoving
      ? favorites.filter(id => id !== appId)
      : [...favorites, appId];
    
    setFavorites(newFavorites);
    localStorage.setItem('pinnacle-favorites', JSON.stringify(newFavorites));
    
    // Show undo notification when removing
    if (isRemoving) {
      const app = allApps.find(a => a.id === appId);
      setUndoNotification({ appId, appName: app?.name, previousFavorites: favorites });
      setTimeout(() => setUndoNotification(null), 5000);
    }
  };

  const undoRemoveFavorite = () => {
    if (undoNotification) {
      setFavorites(undoNotification.previousFavorites);
      localStorage.setItem('pinnacle-favorites', JSON.stringify(undoNotification.previousFavorites));
      setUndoNotification(null);
    }
  };

  const clearAllFavorites = () => {
    setFavorites([]);
    localStorage.setItem('pinnacle-favorites', JSON.stringify([]));
    setShowClearConfirm(false);
  };

  // Drag & Drop handlers for favorites reordering
  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.target);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === dropIndex) return;

    const newFavorites = [...favorites];
    const draggedItem = newFavorites[draggedIndex];
    newFavorites.splice(draggedIndex, 1);
    newFavorites.splice(dropIndex, 0, draggedItem);

    setFavorites(newFavorites);
    localStorage.setItem('pinnacle-favorites', JSON.stringify(newFavorites));
    setDraggedIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  // Lazy loading with Intersection Observer
  const observerRef = useRef(null);

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const cardId = entry.target.getAttribute('data-card-id');
            if (cardId) {
              setVisibleCards((prev) => new Set([...prev, cardId]));
            }
          }
        });
      },
      {
        rootMargin: '50px',
        threshold: 0.1
      }
    );

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  const cardRef = useCallback((node, appId) => {
    if (node && observerRef.current) {
      node.setAttribute('data-card-id', appId);
      observerRef.current.observe(node);
    }
  }, []);

  const getFilteredApps = useMemo(() => {
    const sanitizedQuery = sanitizeInput(searchQuery);

    // Filter by tag if selected
    if (selectedTag) {
      return allApps.filter(app =>
        app.tags && app.tags.some(tag => matchesTag(tag, selectedTag))
      );
    }

    // Filter by search query
    return !sanitizedQuery.trim() ? allApps : allApps.filter((app) => matchesSearchQuery(app, sanitizedQuery));
  }, [searchQuery, selectedTag, allApps, sanitizeInput]);

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setSelectedIndex(-1);
    setSelectedTag(null); // Clear tag filter when searching
  };

  const handleTagClick = (tagLabel) => {
    setSelectedTag(tagLabel);
    setSearchQuery(''); // Clear search when selecting tag
    setSelectedIndex(-1);
  };

  const renderTagIcon = (tag) => {
    if (tag.logoImg) {
      return <img src={tag.logoImg} alt={`${tag.label} logo`} className="tag-logo-img" style={tag.logoStyle} />;
    } else if (tag.iconId) {
      return <span className="tag-icon">{resolveTagIcon(tag.iconId)}</span>;
    }
    return null;
  };

  const handleKeyDown = (e) => {
    const filteredApps = getFilteredApps;
    if (filteredApps.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => (prev < filteredApps.length - 1 ? prev + 1 : prev));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => (prev > 0 ? prev - 1 : -1));
    } else if (e.key === 'Enter' && selectedIndex >= 0) {
      e.preventDefault();
      handleAppClick(filteredApps[selectedIndex]);
    }
  };

  const clearSearch = () => {
    setSearchQuery('');
    setSelectedIndex(-1);
    setSelectedTag(null);
  };

  const getQuickAccessApps = () => {
    // Get favorite apps only
    const favoriteAppsData = favorites.map(id => allApps.find(app => app.id === id)).filter(Boolean);

    // Return favorites (limit to 4)
    return favoriteAppsData.slice(0, 4);
  };

  const firstName = user?.given_name || user?.name?.split(' ')[0] || user?.nickname || 'there';
  const quickAccessApps = getQuickAccessApps();

  // Show nothing while loading auth or redirecting to login
  if (isLoading || !isAuthenticated) {
    return null;
  }

  // Access denied — non-Pinnacle domain
  if (accessDenied) {
    return (
      <div className={`landing-page ${darkMode ? 'dark' : ''}`}>
        <div className="access-denied">
          <Shield size={64} className="access-denied-icon" />
          <h2 className="access-denied-title">
            You don't have access to the Pinnacle Platform
          </h2>
          <p className="access-denied-email">
            Signed in as <strong>{user?.email}</strong>
          </p>
          <p className="access-denied-help">
            Only Pinnacle employees can access this platform. If you believe this is an error, contact support@pinnaclereliability.com
          </p>
          <button
            onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}
            className="access-denied-btn"
          >
            <LogOut size={16} />
            Sign Out
          </button>
        </div>
      </div>
    );
  }

  // Show loading state
  if (loading || permissionsLoading || !appCatalog) {
    return (
      <div className={`landing-page ${darkMode ? 'dark' : ''}`}>
        <div className="loading-container">
          <svg className="loading-logo" viewBox="0 0 500 500" xmlns="http://www.w3.org/2000/svg" style={{ width: 80, height: 80 }}>
            <path fill="rgb(110, 200, 183)" d="M210.378,9.148 L440.233,141.918 L315.404,213.515 L86.932,80.194 L210.378,9.148 Z"/>
            <path fill="rgb(111, 190, 68)" d="M440.233,141.918 L439.559,284.709 L315.404,213.515 L440.233,141.918 Z"/>
            <path fill="rgb(178, 210, 52)" d="M190.337,284.635 L315.404,213.515 L439.559,284.709 C439.823,284.524 86.126,488.566 86.322,488.566 C86.518,488.566 190.104,285.053 190.337,284.635 Z"/>
            <path fill="rgb(237, 29, 54)" d="M86.322,488.566 L86.744,224.115 L189.544,164.642 L190.104,285.053 L86.322,488.566 Z"/>
          </svg>
          <p>Loading applications...</p>
        </div>
      </div>
    );
  }

  const renderAppCard = (app, index, draggable = false) => {
    const isSelected = index === selectedIndex && searchQuery;
    const hasAccess = hasAppAccess(app.id);
    const isRestricted = !hasAccess;
    const isDragging = draggedIndex === index;
    const isVisible = visibleCards.has(app.id);

    return (
      <motion.div
        key={app.id}
        ref={(node) => cardRef(node, app.id)}
        className={`app-card clickable-card ${isSelected ? 'selected' : ''} ${isRestricted ? 'restricted' : ''} ${isDragging ? 'dragging' : ''}`}
        style={{ '--card-accent-color': app.color }}
        initial={{ opacity: 0, y: 20 }}
        animate={isDragging ? false : (isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 })}
        transition={{ duration: 0.3, delay: isVisible ? index * 0.05 : 0 }}
        onClick={() => hasAccess && handleAppClick(app)}
        tabIndex={hasAccess ? 0 : -1}
        role="article"
        aria-label={`${app.name} - ${app.description}${isRestricted ? ' (Access Restricted)' : ''}`}
        draggable={draggable && !isRestricted}
        onDragStart={(e) => draggable && handleDragStart(e, index)}
        onDragOver={(e) => draggable && handleDragOver(e)}
        onDrop={(e) => draggable && handleDrop(e, index)}
        onDragEnd={handleDragEnd}
        onKeyDown={(e) => {
          if (hasAccess && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            handleAppClick(app);
          }
        }}
      >
        {isRestricted && (
          <>
            <div className="restricted-overlay">
              <Shield size={20} />
              <span>Access Restricted</span>
            </div>
            <a
              className="restricted-contact"
              href="mailto:support@pinnaclereliability.com"
              onClick={(e) => e.stopPropagation()}
              aria-label="Request access via email"
            >
              <Mail size={13} />
              <span className="restricted-contact-label">Need access?</span>
              <span className="restricted-contact-email">support@pinnaclereliability.com</span>
            </a>
          </>
        )}

        <button
          className={`favorite-btn ${favorites.includes(app.id) ? 'active' : ''}`}
          onClick={(e) => toggleFavorite(app.id, e)}
          title={favorites.includes(app.id) ? 'Remove from favorites' : 'Add to favorites'}
          aria-label={favorites.includes(app.id) ? `Remove ${app.name} from favorites` : `Add ${app.name} to favorites`}
        >
          <Star size={16} fill={favorites.includes(app.id) ? 'currentColor' : 'none'} />
        </button>

        {!isRestricted && (
          <div className="card-hover-indicator">
            <ArrowRight size={16} />
          </div>
        )}

        <div className="card-icon" style={{ background: `${app.color}18`, color: app.color }}>
          {resolveIcon(app.icon)}
        </div>
        <div className="card-content">
          <h3>{app.name}</h3>
          <p>{app.description}</p>
          {app.contactEmail && (
            <p className="card-contact">
              <a
                href={`mailto:${app.contactEmail}`}
                onClick={(e) => e.stopPropagation()}
                style={{ color: app.color }}
              >
                {app.contactEmail}
              </a>
            </p>
          )}
          {app.customers && app.customers.length > 0 && (
            <div className="customer-badges">
              {app.customers.map((customer) => {
                const branding = companyBranding[customer] || {
                  color: '#64748B',
                  bgColor: '#F1F5F9',
                  logoImg: null
                };
                return (
                  <div
                    key={customer}
                    className="customer-badge"
                    style={{
                      '--customer-color': branding.color,
                      '--customer-bg': branding.bgColor || '#F1F5F9'
                    }}
                    title={`${customer} customers only`}
                  >
                    {branding.logoImg && (
                      <span className="customer-logo-wrapper">
                        <img src={branding.logoImg} alt={`${customer} logo`} className="company-logo-img" />
                      </span>
                    )}
                    <span className="customer-name">{customer}</span>
                  </div>
                );
              })}
            </div>
          )}
          {(app.author || (app.guide && !isRestricted)) && (
            <div className="card-footer">
              {app.guide && !isRestricted && (
                <button
                  className="guide-button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowGuide(app);
                  }}
                  title="How to use this tool"
                  aria-label={`View usage guide for ${app.name}`}
                >
                  <Info size={14} />
                  <span>How to Use</span>
                </button>
              )}
              {app.author && (
                <div className="card-author" style={{ '--author-color': app.color }}>
                  <User size={14} />
                  <span>{app.author}</span>
                </div>
              )}
            </div>
          )}
        </div>
      </motion.div>
    );
  };

  return (
    <div className="landing-page">
      {/* Undo Notification */}
      <AnimatePresence>
        {undoNotification && (
          <motion.div
            className="undo-notification"
            initial={{ opacity: 0, y: -50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -50 }}
          >
            <span>Removed <strong>{undoNotification.appName}</strong> from favorites</span>
            <button onClick={undoRemoveFavorite} aria-label="Undo remove from favorites">Undo</button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Clear Favorites Confirmation */}
      <AnimatePresence>
        {showClearConfirm && (
          <div className="modal-overlay" onClick={() => setShowClearConfirm(false)}>
            <motion.div
              className="confirmation-dialog"
              onClick={(e) => e.stopPropagation()}
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
            >
              <h3>Clear All Favorites?</h3>
              <p>This will remove all {favorites.length} favorited applications.</p>
              <div className="dialog-actions">
                <button onClick={() => setShowClearConfirm(false)} className="cancel-btn">
                  Cancel
                </button>
                <button onClick={clearAllFavorites} className="confirm-btn">
                  Clear All
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Keyboard Shortcuts Help Modal */}
      <AnimatePresence>
        {showKeyboardHelp && (
          <div className="modal-overlay" onClick={() => setShowKeyboardHelp(false)}>
            <motion.div
              className="keyboard-help-dialog"
              onClick={(e) => e.stopPropagation()}
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
            >
              <div className="keyboard-help-header">
                <Keyboard size={24} />
                <h3>Keyboard Shortcuts</h3>
                <button
                  onClick={() => setShowKeyboardHelp(false)}
                  className="close-btn"
                  aria-label="Close keyboard shortcuts"
                >
                  <X size={20} />
                </button>
              </div>
              <div className="keyboard-shortcuts-list">
                <div className="shortcut-item">
                  <kbd>/</kbd>
                  <span>Focus search</span>
                </div>
                <div className="shortcut-item">
                  <kbd>?</kbd>
                  <span>Toggle this help</span>
                </div>
                <div className="shortcut-item">
                  <kbd>Esc</kbd>
                  <span>Clear search / Close modals</span>
                </div>
                <div className="shortcut-item">
                  <kbd>↑</kbd> <kbd>↓</kbd>
                  <span>Navigate search results</span>
                </div>
                <div className="shortcut-item">
                  <kbd>Enter</kbd>
                  <span>Launch selected app</span>
                </div>
              </div>
              <p className="keyboard-help-tip">Press <kbd>?</kbd> anytime to toggle this help</p>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Hero Banner */}
      <section className="hero-banner">
        {/* Aurora color shimmer */}
        <div className="hero-motion-overlay" />

        {/* Animated wave layers */}
        <div className="hero-waves">
          <svg className="hero-wave hero-wave-1" viewBox="0 0 2880 100" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0,50 C240,20 480,80 720,50 C960,20 1200,80 1440,50 C1680,20 1920,80 2160,50 C2400,20 2640,80 2880,50 L2880,100 L0,100 Z" fill="rgba(255,255,255,0.28)" />
          </svg>
          <svg className="hero-wave hero-wave-2" viewBox="0 0 2880 100" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0,60 C360,30 720,85 1080,55 C1440,25 1800,80 2160,55 C2520,30 2880,75 2880,60 L2880,100 L0,100 Z" fill="rgba(255,255,255,0.18)" />
          </svg>
          <svg className="hero-wave hero-wave-3" viewBox="0 0 2880 100" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0,70 C480,40 960,90 1440,55 C1920,20 2400,75 2880,65 L2880,100 L0,100 Z" fill="rgba(255,255,255,0.12)" />
          </svg>
        </div>

        <div className="hero-content">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
          >
            Are you ready to make the world more reliable, {firstName}?
          </motion.h1>
        </div>
      </section>

      {/* Main Content */}
      <main className="main-content">
        <div className="container">
          {/* Search and Controls */}
          <div className="search-section">
            <div className="search-bar" role="search">
              <Search className="search-icon" size={20} aria-hidden="true" />
              <input
                ref={searchInputRef}
                id="app-search"
                type="text"
                placeholder="Search applications... (Press / to focus)"
                value={searchQuery}
                onChange={handleSearchChange}
                onKeyDown={handleKeyDown}
                aria-label="Search applications"
                aria-describedby="search-hint"
              />
              <span id="search-hint" className="sr-only">
                Use arrow keys to navigate results, Enter to launch
              </span>
              {(searchQuery || selectedTag) && (
                <button
                  onClick={clearSearch}
                  className="clear-btn"
                  title="Clear search and filters"
                  aria-label="Clear search and filters"
                >
                  <X size={16} />
                </button>
              )}
              <button
                onClick={() => setShowKeyboardHelp(true)}
                className="keyboard-help-btn"
                title="Keyboard shortcuts (?)"
                aria-label="Show keyboard shortcuts"
              >
                <Keyboard size={18} />
              </button>
              <div className="view-toggle" role="group" aria-label="Layout">
                <button
                  type="button"
                  className={`view-toggle-btn ${viewMode === 'cards' ? 'active' : ''}`}
                  onClick={() => changeViewMode('cards')}
                  aria-pressed={viewMode === 'cards'}
                  title="Card view"
                  aria-label="Card view"
                >
                  <LayoutGrid size={16} />
                </button>
                <button
                  type="button"
                  className={`view-toggle-btn ${viewMode === 'list' ? 'active' : ''}`}
                  onClick={() => changeViewMode('list')}
                  aria-pressed={viewMode === 'list'}
                  title="Grid view"
                  aria-label="Grid view"
                >
                  <List size={16} />
                </button>
              </div>
            </div>

            {/* Quick Tag Filters */}
            {!searchQuery && !selectedTag && (
              <div className="tag-filters">
                <span className="tag-filters-label">Quick access:</span>
                {popularTags.map((tag) => (
                  <button
                    key={tag.label}
                    className={`tag-chip ${tag.logoImg ? 'has-logo' : ''}`}
                    style={{ '--tag-color': tag.color }}
                    onClick={() => handleTagClick(tag.label)}
                    aria-label={`Filter by ${tag.label}`}
                  >
                    {renderTagIcon(tag)}
                    {tag.label}
                  </button>
                ))}
              </div>
            )}

            {/* Active Tag Filter Display */}
            {selectedTag && (
              <div className="active-filter">
                <Tag size={16} />
                <span>Showing apps tagged with: <strong>{selectedTag}</strong></span>
                <button onClick={clearSearch} className="clear-filter-btn" aria-label="Clear filter">
                  <X size={14} />
                </button>
              </div>
            )}
          </div>

          {/* Quick Access Section - Combines Favorites and Recent Apps (only shown when there are items) */}
          {!searchQuery && !selectedTag && quickAccessApps.length > 0 && (
            <section className="section" id="quick-access">
              <div className="section-header">
                <div className="section-icon favorites-icon">
                  <Sparkles size={16} />
                </div>
                <h2>FAVORITES</h2>
                <span className="section-subtitle">Your favorite apps</span>
              </div>
              <div className={`${gridClass} apps-grid`}>
                {quickAccessApps.map((app, index) => renderAppCard(app, index, true))}
              </div>
            </section>
          )}

          {/* Search Results, Tag Filtered Results, or All Apps */}
          {searchQuery || selectedTag ? (
            <section className="section">
              <div className="section-header">
                <div className="section-icon">
                  {selectedTag ? <Tag size={16} /> : <Search size={16} />}
                </div>
                <h2>{selectedTag ? `${selectedTag.toUpperCase()} APPS` : `SEARCH RESULTS`} ({getFilteredApps.length})</h2>
              </div>
              {getFilteredApps.length > 0 ? (
                <div className={`${gridClass} apps-grid`}>
                  {getFilteredApps.map((app, index) => renderAppCard(app, index))}
                </div>
              ) : (
                <div className="empty-state">
                  <SearchX size={64} className="empty-icon" />
                  <h3>No applications found</h3>
                  <p>
                    {selectedTag
                      ? `No apps with tag "${selectedTag}". Try a different filter.`
                      : `No results for "${searchQuery}". Try a different search term.`
                    }
                  </p>
                  <button onClick={clearSearch} className="secondary-btn">
                    Clear {selectedTag ? 'Filter' : 'Search'}
                  </button>
                </div>
              )}
            </section>
          ) : (
            <>
              {/* Applications Section */}
              <section className="section" id="applications">
                <div className="section-header">
                  <div className="section-icon">
                    <Zap size={16} />
                  </div>
                  <h2>ENTERPRISE APPLICATIONS</h2>
                </div>
                <div className={`${gridClass} apps-grid`}>
                  {commonApplications.map((app, index) => renderAppCard(app, index))}
                </div>
              </section>

              {/* Workplace Section - Collapsible */}
              <section className="section workplace-section" id="workplace">
                <button
                  className="section-header collapsible-section-header"
                  onClick={() => setShowWorkplace(!showWorkplace)}
                  aria-expanded={showWorkplace}
                >
                  <div className="section-icon workplace-icon">
                    <Briefcase size={16} />
                  </div>
                  <h2>WORKPLACE</h2>
                  {showWorkplace ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                </button>
                <AnimatePresence>
                  {showWorkplace && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className={`${gridClass} toolbox-grid`}
                    >
                      {commonWorkplace.map((tool, index) => renderAppCard(tool, index))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </section>

              {/* Toolbox Section - Collapsible */}
              <section className="section toolbox-section" id="toolbox">
                <button
                  className="section-header collapsible-section-header"
                  onClick={() => setShowToolbox(!showToolbox)}
                  aria-expanded={showToolbox}
                >
                  <div className="section-icon toolbox-icon">
                    <Wrench size={16} />
                  </div>
                  <h2>TOOLBOX</h2>
                  {showToolbox ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                </button>
                <AnimatePresence>
                  {showToolbox && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className={`${gridClass} toolbox-grid`}
                    >
                      {commonToolbox.map((tool, index) => renderAppCard(tool, index))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </section>

              {/* Client-Specific Section - apps tagged to one or more customers/tenants */}
              {clientApps.length > 0 && (
                <section className="section client-section" id="client-apps">
                  <button
                    className="section-header collapsible-section-header"
                    onClick={() => setShowClientApps(!showClientApps)}
                    aria-expanded={showClientApps}
                  >
                    <div className="section-icon client-icon">
                      <Building2 size={16} />
                    </div>
                    <h2>CLIENT-SPECIFIC APPS</h2>
                    <span className="section-subtitle">Tools built for specific customers</span>
                    {showClientApps ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                  </button>
                  <AnimatePresence>
                    {showClientApps && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className={`${gridClass} toolbox-grid`}
                      >
                        {clientApps.map((app, index) => renderAppCard(app, index))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </section>
              )}
            </>
          )}
        </div>
      </main>

      {/* Usage Guide Modal */}
      {showGuide && (
        <motion.div
          className="modal-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          onClick={() => setShowGuide(null)}
        >
          <motion.div
            className="guide-modal"
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="modal-close"
              onClick={() => setShowGuide(null)}
              aria-label="Close guide"
            >
              <X size={20} />
            </button>

            <div className="guide-header" style={{ '--guide-color': showGuide.color }}>
              <div className="guide-icon" style={{ background: `${showGuide.color}18`, color: showGuide.color }}>
                {resolveIcon(showGuide.icon)}
              </div>
              <div>
                <h2>{showGuide.name}</h2>
                <p className="guide-subtitle">How to Use Guide</p>
              </div>
            </div>

            <div className="guide-content">
              <div className="guide-section">
                <h3>Getting Started</h3>
                <ol className="guide-steps">
                  {showGuide.guide.steps.map((step, index) => (
                    <li key={index}>
                      <span className="step-number">{index + 1}</span>
                      <span className="step-text">{step}</span>
                    </li>
                  ))}
                </ol>
              </div>

              {showGuide.guide.tips && (
                <div className="guide-tips">
                  <div className="tip-icon">💡</div>
                  <div>
                    <h4>Pro Tips</h4>
                    <p>{showGuide.guide.tips}</p>
                  </div>
                </div>
              )}

              {showGuide.author && (
                <div className="guide-author">
                  <User size={16} />
                  <span>Created by <strong>{showGuide.author}</strong></span>
                </div>
              )}
            </div>

            <div className="guide-footer">
              <button
                className="btn-secondary"
                onClick={() => setShowGuide(null)}
              >
                Close
              </button>
              {hasAppAccess(showGuide.id) ? (
                <button
                  className="btn-primary"
                  onClick={() => {
                    handleAppClick(showGuide);
                    setShowGuide(null);
                  }}
                >
                  Open Tool
                </button>
              ) : (
                <button
                  className="btn-primary"
                  disabled
                  style={{ opacity: 0.5, cursor: 'not-allowed' }}
                  title="Access Restricted"
                >
                  🔒 Access Restricted
                </button>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <p>&copy; {new Date().getFullYear()} Pinnacle Reliability. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
